import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { notify } from "../../../App";

const BASE_URL = import.meta.env.VITE_API;

export const getCartFromServer = createAsyncThunk(
  "cart/getFromServer",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().userSlice?.token;
      const res = await fetch(`${BASE_URL}cart`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Failed to fetch cart");

      const data = await res.json();
      return data.data.cart;
    } catch (err) {
      return rejectWithValue(err.message || "Unknown error");
    }
  }
);

// keep single-item update for compatibility
export const updateCartOnServer = createAsyncThunk(
  "cart/updateOnServer",
  async (
    { product_code, variant_code, qty, sum = false },
    { getState, rejectWithValue }
  ) => {
    try {
      const token = getState().userSlice?.token;
      // console.log(product_code);
      const res = await fetch(`${BASE_URL}cart/add`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_code,
          variant_code,
          qty,
          sum: sum, // false → absolute qty
        }),
      });

      if (!res.ok) throw new Error("Failed to update cart");

      return { product_code, variant_code, qty };
    } catch (error) {
      notify("محدودیت خرید آیتم", "error");
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

// NEW: update multiple products in one request
export const updateCartPdOnServer = createAsyncThunk(
  "cart/updatePdOnServer",
  async (cartItems, { getState, rejectWithValue }) => {
    try {
      const token = getState().userSlice?.token;

      const res = await fetch(`${BASE_URL}cart/add`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          cart: cartItems,
          sum:false
        }),
      });

      if (!res.ok) throw new Error("Failed to update cart");

      // return the same array (or you can return server response if it sends updated cart)
      return cartItems;
    } catch (error) {
      notify("محدودیت خرید آیتم", "error");
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

const initialState = {
  list: [],
  status: "idle",
  error: null,
};

const cartSlice = createSlice({
  name: "cartSlice",
  initialState,
  reducers: {
    // set absolute quantity (used by your UI add calls)
    add: (state, action) => {
      const {
        code,
        variant_code,
        quantity = 1,
        limit,
        ...rest
      } = action.payload;

      const idx = state.list.findIndex(
        (e) => e.code === code && e.variant_code === variant_code
      );

      const finalQuantity = Math.max(0, Math.min(quantity, limit ?? quantity));

      if (idx !== -1) {
        // set absolute quantity
        state.list[idx].quantity = finalQuantity;
      } else {
        state.list.push({
          code,
          variant_code,
          quantity: finalQuantity,
          limit,
          ...rest,
        });
      }
    },

    // increase by 1 or add with 1
    addInsideCartPage: (state, action) => {
      const { code, variant_code, limit, ...rest } = action.payload;

      const idx = state.list.findIndex(
        (e) => e.code === code && e.variant_code === variant_code
      );

      if (idx !== -1) {
        const newQ = Math.min(state.list[idx].quantity + 1, limit ?? Infinity);
        state.list[idx].quantity = newQ;
      } else {
        state.list.push({
          code,
          variant_code,
          quantity: 1,
          limit,
          ...rest,
        });
      }
    },
    removeInsideCartPage: (state, action) => {
      const { code, variant_code, quantity = 1 } = action.payload;
      const item = state.list.find(
        (e) => e.code === code && e.variant_code === variant_code
      );

      if (item) {
        item.quantity -= 1;
        if (item.quantity <= 0) {
          state.list = state.list.filter(
            (e) => !(e.code === code && e.variant_code === variant_code)
          );
        }
      }
    },

    remove: (state, action) => {
      const { code, variant_code, quantity = 1 } = action.payload;
      const idx = state.list.findIndex(
        (e) => e.code === code && e.variant_code === variant_code
      );
      if (idx !== -1) {
        state.list[idx].quantity -= quantity;
        if (state.list[idx].quantity <= 0) {
          state.list.splice(idx, 1);
        }
      }
    },

    clear: (state, action) => {
      const { code, variant_code } = action.payload;
      state.list = state.list.filter(
        (e) => !(e.code === code && e.variant_code === variant_code)
      );
    },

    clearFullCart: (state) => {
      state.list = [];
    },
  },

  extraReducers: (builder) => {
    builder
      // GET CART
      .addCase(getCartFromServer.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getCartFromServer.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload ?? [];
      })
      .addCase(getCartFromServer.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        console.error("Failed to fetch cart:", action.payload);
      })

      // UPDATE single
      .addCase(updateCartOnServer.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateCartOnServer.fulfilled, (state, action) => {
        state.status = "succeeded";

        const { product_code, variant_code, qty } = action.payload;

        const itemIndex = state.list.findIndex(
          (item) =>
            item.code === product_code && item.variant_code === variant_code
        );

        if (qty <= 0) {
          if (itemIndex !== -1) state.list.splice(itemIndex, 1);
        } else {
          if (itemIndex !== -1) {
            state.list[itemIndex].quantity = qty;
          } else {
            state.list.push({
              code: product_code,
              variant_code,
              quantity: qty,
            });
          }
        }
      })
      .addCase(updateCartOnServer.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        console.error("Failed to update cart:", action.payload);
      })

      // UPDATE multiple (array)
      .addCase(updateCartPdOnServer.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateCartPdOnServer.fulfilled, (state, action) => {
        state.status = "succeeded";
        const items = action.payload || [];

        // for each returned item, upsert or remove
        items.forEach((it) => {
          // payload shape expected: { product_code, variant_code, qty, sum? }
          const { product_code, variant_code, qty } = it;
          const idx = state.list.findIndex(
            (e) => e.code === product_code && e.variant_code === variant_code
          );

          if (qty <= 0) {
            if (idx !== -1) state.list.splice(idx, 1);
          } else {
            if (idx !== -1) {
              state.list[idx].quantity = qty;
            } else {
              state.list.push({
                code: product_code,
                variant_code,
                quantity: qty,
              });
            }
          }
        });
      })
      .addCase(updateCartPdOnServer.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        console.error("Failed to update cart (bulk):", action.payload);
      });
  },
});

export const { add, addInsideCartPage,removeInsideCartPage, remove, clear, clearFullCart } =
  cartSlice.actions;
export default cartSlice.reducer;
